import { parseScheduleWindows, type TimeWindow, toTimeParts } from '@provision/sidekick-utils';
import { ConditionType } from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import type {
  CustomRuleType,
  SelectorValue,
} from 'src/graphql/entities/behavior/behavior.types';

/** A condition group as the rules form holds it: rules and nested groups. */
interface ConditionsQuery {
  rules?: readonly unknown[];
}

export const EMPTY_SCHEDULE_MESSAGE =
  'Select at least one day for each Custom schedule condition';
export const INVERTED_RANGE_MESSAGE =
  'Each Custom schedule needs an end time after its start time';

/**
 * The conditions' save check: every Custom schedule row needs a day, and an end time after its
 * start time. Either gap leaves a schedule that contains no moment, which never matches as Within
 * and always does as Outside.
 */
export function validateScheduleConditions(group: ConditionsQuery): true | string {
  const windowsOfEachSchedule = collectScheduleWindows(group);

  if (windowsOfEachSchedule.some((windows) => windows.length === 0)) {
    return EMPTY_SCHEDULE_MESSAGE;
  }

  if (windowsOfEachSchedule.some((windows) => windows.some(isInvertedRange))) {
    return INVERTED_RANGE_MESSAGE;
  }

  return true;
}

/** Whether a window's end is not after its start: the time pickers let a typed time through. */
export function isInvertedRange({ timeFrom, timeTo }: TimeWindow): boolean {
  return toMinutes(timeTo) <= toMinutes(timeFrom);
}

function collectScheduleWindows(group: ConditionsQuery): TimeWindow[][] {
  return (group?.rules ?? []).flatMap((rule) => {
    if (isConditionsQuery(rule)) {
      return collectScheduleWindows(rule);
    }

    const { field, value } = rule as CustomRuleType;
    const isSchedule =
      (field as unknown as SelectorValue | undefined)?.conditionType ===
      ConditionType.CustomSchedule;
    return isSchedule ? [parseScheduleWindows(value)] : [];
  });
}

function isConditionsQuery(rule: unknown): rule is ConditionsQuery {
  return typeof rule === 'object' && rule !== null && 'rules' in rule;
}

// `parseScheduleWindows` has already checked both times are clock times
function toMinutes(time: string): number {
  const parts = toTimeParts(time);
  return parts ? parts.hour * 60 + parts.minute : 0;
}
